const getPluginImmediate = () => {
  if (typeof window === 'undefined') return null;
  const w = window;
  if (w.Capacitor && w.Capacitor.Plugins && w.Capacitor.Plugins.EposPlugin) return w.Capacitor.Plugins.EposPlugin;
  if (w.EposPlugin) return w.EposPlugin;
  if (w.Plugins && w.Plugins.EposPlugin) return w.Plugins.EposPlugin;
  return null;
};

const waitForPlugin = (timeout = 20000, interval = 100) => {
  const start = Date.now();
  return new Promise((resolve, reject) => {
    const tryNow = () => {
      const p = getPluginImmediate();
      if (p) return resolve(p);
      if (Date.now() - start >= timeout) {
        const w = typeof window !== 'undefined' ? window : {};
        const available = w && w.Capacitor && w.Capacitor.Plugins ? Object.keys(w.Capacitor.Plugins) : w && w.Plugins ? Object.keys(w.Plugins) : [];
        const err = new Error('EposPlugin not available (timed out)');
        err.details = {
          capacitorPresent: !!(w && w.Capacitor),
          pluginKeys: available,
          userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
        };
        return reject(err);
      }
      setTimeout(tryNow, interval);
    };
    tryNow();
  });
};

const callPlugin = async (methodName, args) => {
  let plugin = getPluginImmediate();
  if (!plugin) {
    // If Capacitor bridge exists but plugin metadata wasn't generated,
    // create a lightweight proxy that calls the native bridge directly.
    try {
      const cap = typeof window !== 'undefined' ? window.Capacitor : undefined;
      if (cap && typeof cap.nativePromise === 'function') {
        plugin = new Proxy({}, {
          get: function (_target, prop) {
            return function (args) { return cap.nativePromise('EposPlugin', prop, args); };
          }
        });
      }
    } catch (e) {
      // ignore
    }
    // Log diagnostic info so it appears in WebView console / adb logcat
    try {
      const w = typeof window !== 'undefined' ? window : {};
      const keys = w && w.Capacitor && w.Capacitor.Plugins ? Object.keys(w.Capacitor.Plugins) : w && w.Plugins ? Object.keys(w.Plugins) : [];
      console.warn('[EposShim] plugin not present immediately', {
        capacitor: !!w.Capacitor,
        keys,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
      });
    } catch (e) {
      // ignore
    }
    plugin = await waitForPlugin().catch((e) => {
      throw e;
    });
  }
  if (!plugin || !plugin[methodName]) {
    const w = typeof window !== 'undefined' ? window : {};
    const available = w && w.Capacitor && w.Capacitor.Plugins ? Object.keys(w.Capacitor.Plugins) : w && w.Plugins ? Object.keys(w.Plugins) : [];
    const err = new Error(`${methodName}: EposPlugin method not available`);
    err.details = { pluginPresent: !!plugin, method: methodName, pluginKeys: available };
    throw err;
  }
  return plugin[methodName](args);
};

const EposPlugin = {
  checkSDKAvailability: () => callPlugin('checkSDKAvailability'),
  searchPrinters: () => callPlugin('searchPrinters'),
  findIpForTarget: (options) => callPlugin('findIpForTarget', options),
  connectPrinter: (options) => callPlugin('connectPrinter', options),
  disconnectPrinter: () => callPlugin('disconnectPrinter'),
  print: (options) => callPlugin('print', options),
  printRawData: (options) => callPlugin('printRawData', options),
  printWithCommands: (options) => callPlugin('printWithCommands', options),
  getStatus: () => callPlugin('getStatus'),
  checkBluetoothState: () => callPlugin('checkBluetoothState'),
  ensureBluetoothEnabled: () => callPlugin('ensureBluetoothEnabled'),
  requestRuntimePermissions: () => callPlugin('requestRuntimePermissions'),
  hasRuntimePermissions: () => callPlugin('hasRuntimePermissions'),
};

const { PrinterBuilder } = require('./PrinterBuilder.js');

if (typeof module !== 'undefined' && module.exports) {
  module.exports = EposPlugin;
  module.exports.default = EposPlugin;
  module.exports.EposPlugin = EposPlugin;
  module.exports.PrinterBuilder = PrinterBuilder;
}

if (typeof exports !== 'undefined') {
  exports.default = EposPlugin;
  exports.EposPlugin = EposPlugin;
  exports.PrinterBuilder = PrinterBuilder;
}
